import { RefObject, useEffect, useState } from 'react'
import {
  AnyStepData,
  InformationStepHandle,
  RenderableScene,
  StepHandle,
  Variant,
  WorkflowExperience
} from '@spiffcommerce/core'
import { SceneMenu } from './SceneMenu/SceneMenu'
import { SummaryScene } from './SummaryScene/SummaryScene'
import { PendantOptions } from './PendantOptions/PendantOptions'
import { Step } from './Step/Step'
import { CustomScene } from '../../types/types'
import { Description } from '../Description/Description'
import { Spinner } from '../Spinner/Spinner'
import { useSpiffClient } from '../../hooks/useSpiffClient'
import { ErrorMessage } from '../ErrorMessage/ErrorMessage'
import { TitleBar } from '../TitleBar/TitleBar'
import { useAppContext } from '../../context/AppContext'
import { getSelectedPendantLabels } from '../../helpers/getSelectedPendantLabels'
import { getLineItems } from '../../helpers/getLineItems'
import { addToCart, dispatchAddToCartEvent } from '../../helpers/addToCart'
import { createWorkflowExperiencesForPendants, updateWorkflowExperienceTransformsForPendants } from '../../helpers/spiffTransforms'

export const SelectionPanel = ({ spiffState, canvasRef }: { spiffState: ReturnType<typeof useSpiffClient>; canvasRef: RefObject<HTMLCanvasElement> }) => {
  const { bundle, previewService, isLoading, clearSelections, initializeBundle, setIsLoading } = spiffState
  const {
    availablePreLoadedPendants,
    setIsLengthScene,
    setIsPendantQuantityScene,
    isPendantScene,
    setIsPendantScene,
    isLastScene,
    setIsLastScene,
    setOnBodyPreviewStep,
    isOnBodyPreview,
    setIsOnBodyPreview
  } = useAppContext()

  const [allWorkflows, setAllWorkflows] = useState<WorkflowExperience[]>([])
  const [allScenes, setAllScenes] = useState<CustomScene[]>([])
  const [currentScene, setCurrentScene] = useState<number>(2) // Start from Length scene
  const [currentSteps, setCurrentSteps] = useState<StepHandle<AnyStepData>[]>([])
  const [showSummaryScene, setShowSummaryScene] = useState<boolean>(false)
  const [showError, setShowError] = useState<boolean>(false)
  const [sceneMenuIsOpen, setSceneMenuIsOpen] = useState(false)
  const [showClearSelectionsPopup, setShowClearSelectionsPopup] = useState(false)
  const [clearSelectionLinkPath, setClearSelectionLinkPath] = useState<string>('')
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [addedToCart, setAddedToCart] = useState(false)

  const collection = bundle?.getProductCollection()
  const baseProductName = allWorkflows[0]?.getWorkflowManager().getProduct().name
  const pendantProducts = collection?.getProducts().filter((product) => product.getResource().productTags?.find((tag) => tag.name === 'pendant'))
  const selectedScene = allScenes[currentScene]
  const pendantSceneLabel = `Pendant #${selectedScene?.workflowPosition}`

  const mapAllScenes = (workflows: WorkflowExperience[]) => {
    const allScenes = workflows.flatMap((workflow: WorkflowExperience, index: number) => {
      const workflowManager = workflow.getWorkflowManager()

      let renderableStepsArray: StepHandle<AnyStepData>[] = []
      const onScenesChange = (scenes: RenderableScene[]) => {
        const renderableSteps = scenes.flatMap((s) => s.renderableSteps)
        const steps = workflow.getSteps().filter((s) => renderableSteps.includes(s.getId()))

        renderableStepsArray = steps.filter((step) => step !== null && !step?.getName().toLowerCase().includes('silent')) as StepHandle<AnyStepData>[]
      }

      // Attach the listener right after the experience is constructed.
      workflow.detachRenderableSceneListener(onScenesChange)
      workflow.attachRenderableSceneListener(onScenesChange)

      const scenes = workflow.getScenes().map((scene) => {
        if (scene.name.toLowerCase().includes('silent')) return

        const steps = scene.stepIds.map((stepId) => workflow.getStepById(stepId))
        const product = workflowManager.getProduct()
        const onBodyPreviewStep = steps.find((step) => step?.getTags().includes('on-body-preview'))

        if (onBodyPreviewStep) setOnBodyPreviewStep(onBodyPreviewStep)

        const variants = steps
          .flatMap((step) => step && step.getCurrentVariant())
          .filter((variant) => variant && !variant?.getName().toLowerCase().includes('colour')) as Variant[]

        const isPendantScene = index !== 0 // TODO: replace this with tags
        const isPendantQuantityScene = steps.find((step) => step?.getTags().includes('pendants'))

        return {
          ...scene,
          workflowPosition: index,
          isPendantScene: isPendantScene ? true : false,
          isPendantQuantityScene: isPendantQuantityScene ? true : false,
          productId: product.id,
          variants,
          steps: steps.filter((step) => step && !step?.getTags().includes('on-body-preview')) as StepHandle<AnyStepData>[],
          renderableSteps: isPendantScene ? renderableStepsArray : null
        }
      })

      return scenes.filter((scene) => scene !== undefined) as CustomScene[]
    })

    if (isFirstLoad) {
      const lengthSceneIndex = allScenes.findIndex((scene) => scene.name.toLowerCase().includes('length'))
      setCurrentScene(lengthSceneIndex)
    }

    setAllScenes(allScenes)
  }

  const getStepsForCurrentScene = () => {
    const selectedScene = allScenes[currentScene]
    if (!selectedScene) return

    setIsLengthScene(selectedScene?.name.toLowerCase().includes('length'))
    setIsPendantScene(selectedScene?.isPendantScene)
    setIsPendantQuantityScene(selectedScene?.isPendantQuantityScene)
    currentScene === allScenes.length - 1 ? setIsLastScene(true) : setIsLastScene(false)

    const currentWorkflow = allWorkflows[selectedScene.workflowPosition]
    if (!currentWorkflow) return

    if (selectedScene.renderableSteps) {
      setCurrentSteps(selectedScene.renderableSteps)
      return
    }

    const currentSteps = allScenes[currentScene].steps.filter(
      (step) => step !== null && !step?.getName().toLowerCase().includes('silent')
    ) as StepHandle<AnyStepData>[]

    setCurrentSteps(currentSteps)
    setIsLoading(false)
  }

  const handleLengthChange = async () => {
    await handlePendantQuantityChange(allWorkflows.length - 1) // -1 to account for base workflow TODO: replace this with tags
  }

  const handlePendantQuantityChange = async (quantity: number) => {
    if (!bundle) return
    setIsLoading(true)

    const currentPendantWorkflows = allWorkflows.filter((workflow) => {
      const workflowManager = workflow.getWorkflowManager()
      const product = workflowManager.getProduct()
      const isBaseProduct = product.name.includes('Necklace') || product.name.includes('Bracelet') // TODO: replace this with tags

      return !isBaseProduct
    })

    // eject current pendant workflows from preview service
    currentPendantWorkflows.map((pendant) => {
      pendant.getWorkflowManager().ejectFromPreviewService()
    })

    await bundle.removeWorkflowExperiences(currentPendantWorkflows)

    if (!pendantProducts) return

    const pendantWorkflowExperienceAndTransforms = await createWorkflowExperiencesForPendants({
      baseWorkflowExperience: allWorkflows[0],
      quantity,
      bundle,
      getProduct: (index) => {
        const product = pendantProducts.find((product) =>
          product
            .getName()
            .toLowerCase()
            .includes(availablePreLoadedPendants[index % availablePreLoadedPendants.length])
        )
        return product
      }
    })

    if (!pendantWorkflowExperienceAndTransforms) {
      console.error('no workflow experience and transforms')
      return
    }
    const pendantWorkflowExperiences = pendantWorkflowExperienceAndTransforms.map((item) => item.workflowExperience)
    await bundle.addWorkflowExperiences(pendantWorkflowExperiences)
    await updateWorkflowExperienceTransformsForPendants({
      workflowExperienceAndTransforms: pendantWorkflowExperienceAndTransforms,
      isOnBodyPreview: isOnBodyPreview,
      bundle,
      previewService
    })
  }

  const bundleInit = () => {
    if (!bundle) return
    const getWorkflowExperiences = bundle.getWorkflowExperiences()

    if (!getWorkflowExperiences.length) return
    mapAllScenes(getWorkflowExperiences)
    setAllWorkflows(getWorkflowExperiences)
  }

  const handleAddToCart = async () => {
    if (!bundle || !previewService) return
    setAddedToCart(true)
    setIsLoading(true)

    const selectedPendantLabels = getSelectedPendantLabels(allScenes)
    const finalisedBundle = await bundle.finish()
    const lineItems = getLineItems(finalisedBundle.items, selectedPendantLabels, baseProductName)

    const cartItem = await addToCart({
      items: lineItems
    })

    if (!cartItem) return setIsLoading(false) // TODO: error message

    if (cartItem.status === 'bad_request') {
      setShowError(true)

      setTimeout(() => {
        setShowError(false)
      }, 3000)

      return setIsLoading(false)
    }

    // Fire the event to open the cart drawer
    dispatchAddToCartEvent(baseProductName)

    setOnBodyPreviewStep(null)
    setIsOnBodyPreview(false)
    await initializeBundle()
    setIsLoading(false)
    setAddedToCart(false)
    setCurrentScene(2) // Start from Length scene
    setShowSummaryScene(false)
  }

  const getSceneLabel = () => {
    if (showSummaryScene) return 'Summary'
    if (isPendantScene) return `Choose ${pendantSceneLabel}`
    return `Choose ${selectedScene?.name}`
  }

  const InformationStep = ({ content }: { content: string }) => {
    return <div className="mt-2.5 px-0 text-sm font-calibre">{content}</div>
  }

  useEffect(() => {
    bundleInit()
  }, [bundle])

  useEffect(() => {
    getStepsForCurrentScene()
  }, [currentScene, allWorkflows])

  if (!bundle) return <></>

  return (
    <div className="md:relative bg-background-light md:min-w-[400px] lg:min-w-[493px] w-full md:w-[34vw] border-l border-default md:overflow-hidden">
      {isLoading && <Spinner />}
      {showError && <ErrorMessage message="There was an error adding your item to the cart. Please try again." />}
      <TitleBar bundle={bundle} baseProductName={baseProductName} isLoading={isLoading} className="hidden md:flex" />
      <SceneMenu
        bundle={bundle}
        previewService={previewService}
        allScenes={allScenes}
        currentScene={currentScene}
        setCurrentScene={setCurrentScene}
        sceneMenuIsOpen={sceneMenuIsOpen}
        setSceneMenuIsOpen={setSceneMenuIsOpen}
        showSummaryScene={showSummaryScene}
        setShowSummaryScene={setShowSummaryScene}
        showClearSelectionsPopup={showClearSelectionsPopup}
        setShowClearSelectionsPopup={setShowClearSelectionsPopup}
        clearSelectionLinkPath={clearSelectionLinkPath}
        handleAddToCart={handleAddToCart}
        clearSelections={clearSelections}
        canvasRef={canvasRef}
        addedToCart={addedToCart}
      />


      <div className="px-5 mt-5">
        <h3 className="h6 m-0">{getSceneLabel()}</h3>
        {currentSteps?.map((step, i) => {
          const stepType = step.getType()
          if (stepType === 'Information') {
            const informationStep = step as InformationStepHandle

            return <InformationStep content={informationStep.getContent()} />
          }
        })}
      </div>

      {showSummaryScene && (
        <SummaryScene
          bundle={bundle}
          allScenes={allScenes}
          addedToCart={addedToCart}
          handleAddToCart={handleAddToCart}
          setCurrentScene={setCurrentScene}
          setShowSummaryScene={setShowSummaryScene}
        />
      )}

      {!showSummaryScene && (
        <>
          {isPendantScene && pendantProducts && (
            <PendantOptions
              currentScene={allScenes[currentScene]}
              bundle={bundle}
              allWorkflows={allWorkflows}
              pendantProducts={pendantProducts}
              workflowPosition={allScenes[currentScene].workflowPosition}
              sceneProductId={allScenes[currentScene].productId}
              previewService={previewService}
              setIsLoading={setIsLoading}
              bundleInit={bundleInit}
              isOnBodyPreview={isOnBodyPreview}
            />
          )}
          <div className="font-calibre px-5 mb-5 lg:mb-[30px]">
            {currentSteps?.map((step, i) => (
              <Step
                key={i}
                step={step}
                isPendantScene={isPendantScene}
                allWorkflows={allWorkflows}
                isFirstLoad={isFirstLoad}
                setIsFirstLoad={setIsFirstLoad}
                handleLengthChange={handleLengthChange}
                handlePendantQuantityChange={handlePendantQuantityChange}
                bundleInit={bundleInit}
                setIsLoading={setIsLoading}
                setShowClearSelectionsPopup={setShowClearSelectionsPopup}
                setSceneMenuIsOpen={setSceneMenuIsOpen}
                setClearSelectionLinkPath={setClearSelectionLinkPath}
              />
            ))}

            {isLastScene && <button
              disabled={addedToCart}
              onClick={handleAddToCart}
              className="Button min-h-[35px] lg:min-h-[44px] bg-ink text-white border border-solid border-deep-grey rounded-[2px] py-2 px-3 lg:py-3 mb-7 lg:mt-2.5 lg:mb-10 w-full flex justify-center uppercase disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Add to Bag</span>
            </button>}
          </div>
        </>
      )}
      <Description />
    </div>
  )
}
